import React, { useState, useEffect, useRef } from 'react';
import { api } from './api';
import LiveNow, { useTimetable } from './LiveNow';
import TimetableView from './TimetableView';
import { formatDay, isTimetableAvailable } from './timetableUtils';
import './Schedule.css';

export default function Schedule() {
  const [scheduleType, setScheduleType] = useState('main'); // 'main' или 'extracurricular'
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasFile, setHasFile] = useState(false);
  const activeRowSpansRef = useRef({});
  // Основное расписание из МЭШ, разобранное сервером в уроки
  const timetable = useTimetable();

  useEffect(() => {
    loadSheets();
  }, [scheduleType]);

  useEffect(() => {
    if (selectedSheet) {
      loadSheetData(selectedSheet);
    } else if (sheets.length > 0) {
      setSelectedSheet(sheets[0]);
    }
  }, [selectedSheet, sheets, scheduleType]);

  const loadSheets = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getScheduleSheets(scheduleType);
      if (data.has_file && data.sheets && data.sheets.length > 0) {
        setSheets(data.sheets);
        setHasFile(true);
        if (data.current_sheet) {
          setSelectedSheet(data.current_sheet);
        }
      } else {
        setSheets([]);
        setHasFile(false);
      }
    } catch (err) {
      console.error('Error loading schedule sheets:', err);
      setError(err.message || 'Не удалось загрузить расписание');
      setSheets([]);
      setHasFile(false);
    } finally {
      setLoading(false);
    }
  };

  const loadSheetData = async (sheetName) => {
    try {
      setLoading(true);
      setError(null);
      // Сбрасываем активные rowspan при загрузке новых данных
      activeRowSpansRef.current = {};
      const data = await api.getScheduleSheetData(sheetName, scheduleType);
      setTableData(data.data || []);
      setHeaders(data.headers || []);
    } catch (err) {
      setError(err.message || 'Не удалось загрузить данные листа');
    } finally {
      setLoading(false);
    }
  };

  const typeSelector = (
          <div className="schedule-type-selector">
            <button
              className={`schedule-type-button ${scheduleType === 'main' ? 'active' : ''}`}
              onClick={() => {
                setScheduleType('main');
                setSelectedSheet(null);
              }}
            >
              Основное расписание
            </button>
            <button
              className={`schedule-type-button ${scheduleType === 'extracurricular' ? 'active' : ''}`}
              onClick={() => {
                setScheduleType('extracurricular');
                setSelectedSheet(null);
              }}
            >
              Внеурочные занятия
            </button>
          </div>
  );

  const header = (
    <div className="schedule-header">
      <div className="schedule-title-row">
        <h2 className="schedule-title">Расписание</h2>
        {typeSelector}
      </div>
    </div>
  );

  // Основное расписание из МЭШ: доступно, скрыто администратором или устарело
  if (scheduleType === 'main' && timetable && (timetable.lessons.length > 0 || timetable.hidden || timetable.expired)) {
    const available = isTimetableAvailable(timetable);
    // Сервер отдаёт уроки скрытого/устаревшего расписания только администратору
    const adminPreview = !available && timetable.available === false && timetable.lessons.length > 0;
    const expiredText = timetable.valid_until
      ? `Расписание было актуально до ${formatDay(timetable.valid_until)}.`
      : 'Срок актуальности расписания истёк.';

    if (!available && !adminPreview) {
      return (
        <div className="schedule-container">
          {header}
          <div className="schedule-empty">
            <h3>{timetable.hidden ? 'Расписание временно скрыто' : 'Расписание устарело'}</h3>
            <p>
              {timetable.hidden
                ? 'Администратор скрыл расписание — скоро оно снова появится.'
                : `${expiredText} Новое расписание скоро загрузят.`}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="schedule-container">
        {header}
        {adminPreview && (
          <div className="schedule-admin-note">
            {timetable.hidden
              ? 'Расписание скрыто от учителей — его видят только администраторы. Включить можно в админ-панели.'
              : `${expiredText} Учителя его не видят — загрузите новое или продлите дату в админ-панели.`}
          </div>
        )}
        {!adminPreview && timetable.valid_until && (
          <div className="schedule-valid-note">Актуально до {formatDay(timetable.valid_until)}</div>
        )}
        {!adminPreview && <LiveNow timetable={timetable} />}
        <TimetableView timetable={timetable} />
      </div>
    );
  }

  if (loading && !hasFile && !error) {
    return (
      <div className="schedule-container">
        <div className="schedule-loading">Загрузка расписания...</div>
      </div>
    );
  }

  if (error && !hasFile) {
    return (
      <div className="schedule-container">
        <div className="schedule-error">
          <h3>Ошибка загрузки расписания</h3>
          <p>{error}</p>
          <p style={{ marginTop: '16px', fontSize: '14px', color: 'var(--eu-text-muted)' }}>
            Расписание не загружено. Обратитесь к администратору для загрузки расписания.
          </p>
        </div>
      </div>
    );
  }

  if (!hasFile && !loading) {
    return (
      <div className="schedule-container">
        <div className="schedule-empty">
          <h3>Расписание не загружено</h3>
          <p>В системе нет файла расписания.</p>
          <p style={{ marginTop: '16px', fontSize: '14px', color: 'var(--eu-text-muted)' }}>
            Обратитесь к администратору для загрузки расписания через админ-панель.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="schedule-container">
      <div className="schedule-header">
        <div className="schedule-title-row">
          <h2 className="schedule-title">Расписание</h2>
          <div className="schedule-type-selector">
            <button
              className={`schedule-type-button ${scheduleType === 'main' ? 'active' : ''}`}
              onClick={() => {
                setScheduleType('main');
                setSelectedSheet(null);
              }}
            >
              Основное расписание
            </button>
            <button
              className={`schedule-type-button ${scheduleType === 'extracurricular' ? 'active' : ''}`}
              onClick={() => {
                setScheduleType('extracurricular');
                setSelectedSheet(null);
              }}
            >
              Внеурочные занятия
            </button>
          </div>
        </div>
        {sheets.length > 0 && (
          <div className="schedule-sheets">
            {sheets.map((sheetName) => (
              <button
                key={sheetName}
                className={`schedule-class-button ${selectedSheet === sheetName ? 'active' : ''}`}
                onClick={() => setSelectedSheet(sheetName)}
              >
                {sheetName}
              </button>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="schedule-error-message">
          {error}
        </div>
      )}

      {loading ? (
        <div className="schedule-loading">Загрузка данных...</div>
      ) : tableData.length > 0 ? (
        <div className="schedule-table-wrapper">
          <table className="schedule-excel-table">
            <thead>
              <tr>
                {headers.map((header, index) => (
                  <th key={index} className="schedule-excel-header">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, rowIndex) => {
                // Отслеживаем активные rowspan для текущей строки
                const activeRowSpans = activeRowSpansRef.current;
                
                return (
                  <tr key={rowIndex}>
                    {Array.isArray(row) ? (
                      // Новый формат: массив ячеек
                      (() => {
                        let actualColIndex = 0;
                        return row.map((cellData, colIndex) => {
                          // Пропускаем ячейки, которые заняты rowspan из предыдущих строк
                          while (activeRowSpans[actualColIndex] && activeRowSpans[actualColIndex] > 0) {
                            activeRowSpans[actualColIndex]--;
                            if (activeRowSpans[actualColIndex] === 0) {
                              delete activeRowSpans[actualColIndex];
                            }
                            actualColIndex++;
                          }
                          
                          // Пропускаем None (объединенные ячейки)
                          if (cellData === null || cellData === undefined) {
                            actualColIndex++;
                            return null;
                          }
                          
                          const cellText = cellData.text || '';
                          const teachers = cellData.teachers || [];
                          const formatting = cellData.formatting || {};
                          const colspan = cellData.colspan || 1;
                          const rowspan = cellData.rowspan || 1;
                          
                          // Сохраняем rowspan для следующих строк
                          if (rowspan > 1) {
                            activeRowSpans[actualColIndex] = rowspan - 1;
                          }
                          
                          // Пропускаем колонки, занятые colspan
                          const currentColIndex = actualColIndex;
                          for (let i = 1; i < colspan; i++) {
                            actualColIndex++;
                          }
                          actualColIndex++;
                          
                          // Строим стили на основе форматирования
                          const cellStyle = {
                            backgroundColor: 'var(--eu-surface)', // Фон ячейки по умолчанию (зависит от темы)
                          };
                          if (formatting.bg_color && formatting.bg_color !== '#FFFFFF' && formatting.bg_color !== '#ffffff') {
                            cellStyle.backgroundColor = formatting.bg_color;
                          }
                          if (formatting.font_color) {
                            cellStyle.color = formatting.font_color;
                          }
                          if (formatting.font_bold) {
                            cellStyle.fontWeight = 'bold';
                          }
                          if (formatting.font_italic) {
                            cellStyle.fontStyle = 'italic';
                          }
                          if (formatting.font_size) {
                            cellStyle.fontSize = `${formatting.font_size}px`;
                          }
                          // Выравнивание по центру по умолчанию, если не задано в Excel
                          if (formatting.alignment_horizontal) {
                            cellStyle.textAlign = formatting.alignment_horizontal;
                          } else {
                            cellStyle.textAlign = 'center';
                          }
                          if (formatting.alignment_vertical) {
                            cellStyle.verticalAlign = formatting.alignment_vertical;
                          } else {
                            cellStyle.verticalAlign = 'middle';
                          }
                          
                          return (
                            <td 
                              key={colIndex} 
                              className="schedule-excel-cell"
                              style={cellStyle}
                              colSpan={colspan > 1 ? colspan : undefined}
                              rowSpan={rowspan > 1 ? rowspan : undefined}
                            >
                              {cellText && (
                                <div className="schedule-cell-subject">{cellText}</div>
                              )}
                              {teachers && teachers.length > 0 && (
                                <div className="schedule-cell-teachers">
                                  {teachers.map((teacher, teacherIndex) => (
                                    <div key={teacherIndex} className="schedule-teacher-badge">
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                        <circle cx="12" cy="7" r="4"></circle>
                                      </svg>
                                      <span>{teacher}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </td>
                          );
                        });
                      })()
                    ) : (
                      // Старый формат: объект с ключами-заголовками (для обратной совместимости)
                      (() => {
                        let actualColIndex = 0;
                        return headers.map((header, colIndex) => {
                          // Пропускаем ячейки, которые заняты rowspan
                          while (activeRowSpans[actualColIndex] && activeRowSpans[actualColIndex] > 0) {
                            activeRowSpans[actualColIndex]--;
                            if (activeRowSpans[actualColIndex] === 0) {
                              delete activeRowSpans[actualColIndex];
                            }
                            actualColIndex++;
                          }
                          
                          const cellData = row[header];
                          const isProcessed = cellData && typeof cellData === 'object' && 'teachers' in cellData;
                          
                          if (!isProcessed && !cellData) {
                            actualColIndex++;
                            return <td key={colIndex} className="schedule-excel-cell"></td>;
                          }
                          
                          const cellText = isProcessed ? cellData.text : (cellData || '');
                          const teachers = isProcessed ? cellData.teachers : [];
                          const formatting = isProcessed ? (cellData.formatting || {}) : {};
                          const colspan = isProcessed ? (cellData.colspan || 1) : 1;
                          const rowspan = isProcessed ? (cellData.rowspan || 1) : 1;
                          
                          if (rowspan > 1) {
                            activeRowSpans[actualColIndex] = rowspan - 1;
                          }
                          
                          const currentColIndex = actualColIndex;
                          for (let i = 1; i < colspan; i++) {
                            actualColIndex++;
                          }
                          actualColIndex++;
                          
                          const cellStyle = {
                            backgroundColor: 'var(--eu-surface)', // Фон ячейки по умолчанию (зависит от темы)
                          };
                          if (formatting.bg_color && formatting.bg_color !== '#FFFFFF' && formatting.bg_color !== '#ffffff') {
                            cellStyle.backgroundColor = formatting.bg_color;
                          }
                          if (formatting.font_color) {
                            cellStyle.color = formatting.font_color;
                          }
                          if (formatting.font_bold) {
                            cellStyle.fontWeight = 'bold';
                          }
                          if (formatting.font_italic) {
                            cellStyle.fontStyle = 'italic';
                          }
                          if (formatting.font_size) {
                            cellStyle.fontSize = `${formatting.font_size}px`;
                          }
                          // Выравнивание по центру по умолчанию, если не задано в Excel
                          if (formatting.alignment_horizontal) {
                            cellStyle.textAlign = formatting.alignment_horizontal;
                          } else {
                            cellStyle.textAlign = 'center';
                          }
                          if (formatting.alignment_vertical) {
                            cellStyle.verticalAlign = formatting.alignment_vertical;
                          } else {
                            cellStyle.verticalAlign = 'middle';
                          }
                          
                          return (
                            <td 
                              key={colIndex} 
                              className="schedule-excel-cell"
                              style={cellStyle}
                              colSpan={colspan > 1 ? colspan : undefined}
                              rowSpan={rowspan > 1 ? rowspan : undefined}
                            >
                              {cellText && (
                                <div className="schedule-cell-subject">{cellText}</div>
                              )}
                              {teachers && teachers.length > 0 && (
                                <div className="schedule-cell-teachers">
                                  {teachers.map((teacher, teacherIndex) => (
                                    <div key={teacherIndex} className="schedule-teacher-badge">
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                        <circle cx="12" cy="7" r="4"></circle>
                                      </svg>
                                      <span>{teacher}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </td>
                          );
                        });
                      })()
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
